import React, { useEffect, useState } from 'react';
import { PlusIcon, SearchIcon, FilterIcon, MoreVerticalIcon, ShieldIcon, UserIcon, UsersIcon, CheckCircleIcon, XCircleIcon, EditIcon, TrashIcon, KeyIcon, XIcon } from 'lucide-react';
import { PermissionsEditor } from './users/PermissionsEditor';
interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  department: string;
  status: 'Active' | 'Inactive';
  lastLogin: string;
  createdAt: string;
  assignedModules: string[];
}
interface UserGroup {
  id: string;
  name: string;
  description: string;
  memberCount: number;
  permissions: string[];
  createdAt: string;
}
interface UserManagementProps {
  locale?: string;
}
export function UserManagement({
  locale = 'en-US'
}: UserManagementProps) {
  const [activeTab, setActiveTab] = useState('users');
  const [showPermissionsEditor, setShowPermissionsEditor] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showGroupForm, setShowGroupForm] = useState(false);
  const [showUserForm, setShowUserForm] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [actionMenuOpen, setActionMenuOpen] = useState<string | null>(null);
  // Read licensed modules from localStorage (same source as License Manager)
  const [licensedModules, setLicensedModules] = useState(() => {
    const savedModules = localStorage.getItem('licenseManagerModules');
    if (savedModules) {
      try {
        const modules = JSON.parse(savedModules);
        return modules.map((m: any) => ({
          id: m.id,
          name: m.name,
          status: m.status
        }));
      } catch (e) {
        console.error('Failed to parse licensed modules:', e);
        return [];
      }
    }
    return [];
  });
  // Listen for localStorage changes to keep in sync with License Manager
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'licenseManagerModules' && e.newValue) {
        try {
          const modules = JSON.parse(e.newValue);
          setLicensedModules(modules.map((m: any) => ({
            id: m.id,
            name: m.name,
            status: m.status
          })));
        } catch (error) {
          console.error('Failed to sync licensed modules:', error);
        }
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);
  // Also check for updates when component becomes visible
  useEffect(() => {
    const syncModules = () => {
      const savedModules = localStorage.getItem('licenseManagerModules');
      if (savedModules) {
        try {
          const modules = JSON.parse(savedModules);
          setLicensedModules(modules.map((m: any) => ({
            id: m.id,
            name: m.name,
            status: m.status
          })));
        } catch (e) {
          console.error('Failed to sync modules:', e);
        }
      }
    };
    // Sync when tab becomes active
    syncModules();
  }, [activeTab]);
  const [users] = useState<User[]>([{
    id: '1',
    name: 'John Smith',
    email: 'john.smith@company.com',
    role: 'Administrator',
    department: 'IT',
    status: 'Active',
    lastLogin: '2024-01-15T10:30:00Z',
    createdAt: '2023-01-10T08:00:00Z',
    assignedModules: ['accounts-payable', 'vendor-onboarding', 'purchase-order']
  }, {
    id: '2',
    name: 'Sarah Johnson',
    email: 'sarah.johnson@company.com',
    role: 'Manager',
    department: 'Finance',
    status: 'Active',
    lastLogin: '2024-01-14T16:45:00Z',
    createdAt: '2023-02-15T09:00:00Z',
    assignedModules: ['accounts-payable', 'contract-management']
  }, {
    id: '3',
    name: 'Michael Brown',
    email: 'michael.brown@company.com',
    role: 'User',
    department: 'Operations',
    status: 'Active',
    lastLogin: '2024-01-13T14:20:00Z',
    createdAt: '2023-03-20T10:00:00Z',
    assignedModules: ['purchase-order', 'expense-management']
  }, {
    id: '4',
    name: 'Emily Davis',
    email: 'emily.davis@company.com',
    role: 'User',
    department: 'HR',
    status: 'Inactive',
    lastLogin: '2023-12-28T11:15:00Z',
    createdAt: '2023-04-05T11:00:00Z',
    assignedModules: []
  }]);
  const [userGroups, setUserGroups] = useState<UserGroup[]>([{
    id: 'group-1',
    name: 'Finance Team',
    description: 'Financial department users with AP/AR access',
    memberCount: 8,
    permissions: ['view-invoices', 'create-invoices', 'approve-payments'],
    createdAt: '2023-01-15T09:00:00Z'
  }, {
    id: 'group-2',
    name: 'Procurement Officers',
    description: 'Users with procurement and purchasing authority',
    memberCount: 5,
    permissions: ['create-requisitions', 'create-pos', 'approve-requisitions'],
    createdAt: '2023-02-20T10:00:00Z'
  }, {
    id: 'group-3',
    name: 'Managers',
    description: 'Department managers with approval authority',
    memberCount: 12,
    permissions: ['approve-invoices', 'view-reports', 'manage-team'],
    createdAt: '2023-03-10T11:00:00Z'
  }]);
  const handleEditPermissions = (user: User) => {
    setSelectedUser(user);
    setShowPermissionsEditor(true);
  };
  const handleSavePermissions = (permissions: any) => {
    console.log('Saving permissions for user:', selectedUser?.name, permissions);
    setShowPermissionsEditor(false);
    setSelectedUser(null);
  };
  const handleEditUser = (user: User) => {
    console.log('Editing user:', user);
    alert(`Edit user functionality would open a form for ${user.name}`);
  };
  const handleResetPassword = (user: User) => {
    console.log('Resetting password for:', user);
    alert(`Password reset email would be sent to ${user.email}`);
  };
  const handleDeleteUser = (user: User) => {
    if (confirm(`Are you sure you want to delete ${user.name}?`)) {
      console.log('Deleting user:', user);
      alert(`User ${user.name} would be deleted`);
    }
  };
  const handleEditGroup = (group: UserGroup) => {
    console.log('Editing group:', group);
    alert(`Edit group functionality would open a form for ${group.name}`);
  };
  const handleDeleteGroup = (group: UserGroup) => {
    if (confirm(`Are you sure you want to delete ${group.name}?`)) {
      console.log('Deleting group:', group);
      alert(`Group ${group.name} would be deleted`);
    }
  };
  const handleAddUser = () => {
    setShowUserForm(true);
    console.log('Opening add user form');
  };
  const handleAddGroup = () => {
    setShowGroupForm(true);
    console.log('Opening add group form');
  };
  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) || user.email.toLowerCase().includes(searchTerm.toLowerCase()) || user.department.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || user.status === statusFilter;
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    const matchesDepartment = departmentFilter === 'all' || user.department === departmentFilter;
    return matchesSearch && matchesStatus && matchesRole && matchesDepartment;
  });
  const filteredGroups = userGroups.filter(group => group.name.toLowerCase().includes(searchTerm.toLowerCase()));
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  return <div data-id="element-20724">
      <div className="mb-8 flex justify-between items-center" data-id="element-20725">
        <div data-id="element-20726">
          <h2 className="text-2xl font-bold text-gray-900" data-id="element-20727">User Management</h2>
          <p className="mt-1 text-sm text-gray-500" data-id="element-20728">
            Manage users, groups, roles, and permissions
          </p>
        </div>
      </div>
      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6" data-id="element-20729">
        <nav className="-mb-px flex space-x-8" data-id="element-20730">
          <button onClick={() => setActiveTab('users')} className={`pb-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'users' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-20731">
            <UserIcon className="h-5 w-5 inline mr-2" data-id="element-20732" />
            Users
          </button>
          <button onClick={() => setActiveTab('groups')} className={`pb-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'groups' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-20733">
            <UsersIcon className="h-5 w-5 inline mr-2" data-id="element-20734" />
            User Groups
          </button>
        </nav>
      </div>
      {/* Users Tab */}
      {activeTab === 'users' && <div data-id="element-20735">
          {/* Search and Actions */}
          <div className="mb-6 flex justify-between items-center" data-id="element-20736">
            <div className="flex-1 max-w-md" data-id="element-20737">
              <div className="relative rounded-md shadow-sm" data-id="element-20738">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-20739">
                  <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-20740" />
                </div>
                <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" placeholder="Search users..." data-id="element-20741" />
              </div>
            </div>
            <div className="flex space-x-2" data-id="element-20742">
              <button onClick={() => setShowFilters(!showFilters)} className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-20743">
                <FilterIcon className="h-4 w-4 mr-1" data-id="element-20744" />
                Filters
              </button>
              <button onClick={handleAddUser} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-20745">
                <PlusIcon className="h-4 w-4 mr-1" data-id="element-20746" />
                Add User
              </button>
            </div>
          </div>
          {/* Filters */}
          {showFilters && <div className="mb-6 bg-white p-4 rounded-lg shadow" data-id="element-20747">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-20748">
                <div data-id="element-20749">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-20750">
                    Status
                  </label>
                  <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-20751">
                    <option value="all" data-id="element-20752">All Statuses</option>
                    <option value="Active" data-id="element-20753">Active</option>
                    <option value="Inactive" data-id="element-20754">Inactive</option>
                  </select>
                </div>
                <div data-id="element-20755">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-20756">
                    Role
                  </label>
                  <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)} className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-20757">
                    <option value="all" data-id="element-20758">All Roles</option>
                    <option value="Administrator" data-id="element-20759">Administrator</option>
                    <option value="Manager" data-id="element-20760">Manager</option>
                    <option value="User" data-id="element-20761">User</option>
                  </select>
                </div>
                <div data-id="element-20762">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-20763">
                    Department
                  </label>
                  <select value={departmentFilter} onChange={e => setDepartmentFilter(e.target.value)} className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-20764">
                    <option value="all" data-id="element-20765">All Departments</option>
                    <option value="IT" data-id="element-20766">IT</option>
                    <option value="Finance" data-id="element-20767">Finance</option>
                    <option value="Operations" data-id="element-20768">Operations</option>
                    <option value="HR" data-id="element-20769">HR</option>
                  </select>
                </div>
              </div>
            </div>}
          {/* Users Table */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-20770">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-20771">
              <thead className="bg-gray-50" data-id="element-20772">
                <tr data-id="element-20773">
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-20774">
                    User
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-20775">
                    Role
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-20776">
                    Department
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-20777">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-20778">
                    Last Login
                  </th>
                  <th scope="col" className="relative px-6 py-3" data-id="element-20779">
                    <span className="sr-only" data-id="element-20780">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-20781">
                {filteredUsers.map(user => <tr key={user.id} className="hover:bg-gray-50" data-id="element-20782">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-20783">
                      <div className="flex items-center" data-id="element-20784">
                        <div className="flex-shrink-0 h-10 w-10" data-id="element-20785">
                          <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center" data-id="element-20786">
                            <UserIcon className="h-6 w-6 text-indigo-600" data-id="element-20787" />
                          </div>
                        </div>
                        <div className="ml-4" data-id="element-20788">
                          <div className="text-sm font-medium text-gray-900" data-id="element-20789">
                            {user.name}
                          </div>
                          <div className="text-sm text-gray-500" data-id="element-20790">
                            {user.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-20791">
                      <div className="text-sm text-gray-900" data-id="element-20792">{user.role}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-20793">
                      <div className="text-sm text-gray-900" data-id="element-20794">
                        {user.department}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-20795">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${user.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`} data-id="element-20796">
                        {user.status === 'Active' ? <CheckCircleIcon className="h-4 w-4 mr-1" data-id="element-20797" /> : <XCircleIcon className="h-4 w-4 mr-1" data-id="element-20798" />}
                        {user.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-20799">
                      {formatDate(user.lastLogin)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-20800">
                      <div className="flex justify-end space-x-2" data-id="element-20801">
                        <button onClick={() => handleEditPermissions(user)} className="text-indigo-600 hover:text-indigo-900" title="Edit Permissions" data-id="element-20802">
                          <ShieldIcon className="h-5 w-5" data-id="element-20803" />
                        </button>
                        <button onClick={() => handleEditUser(user)} className="text-gray-600 hover:text-gray-900" title="Edit User" data-id="element-20804">
                          <EditIcon className="h-5 w-5" data-id="element-20805" />
                        </button>
                        <button onClick={() => handleResetPassword(user)} className="text-yellow-600 hover:text-yellow-900" title="Reset Password" data-id="element-20806">
                          <KeyIcon className="h-5 w-5" data-id="element-20807" />
                        </button>
                        <div className="relative" data-id="element-20808">
                          <button onClick={() => setActionMenuOpen(actionMenuOpen === user.id ? null : user.id)} className="text-gray-400 hover:text-gray-600" data-id="element-20809">
                            <MoreVerticalIcon className="h-5 w-5" data-id="element-20810" />
                          </button>
                          {actionMenuOpen === user.id && <div className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10" data-id="element-20811">
                              <div className="py-1" data-id="element-20812">
                                <button onClick={() => {
                          handleDeleteUser(user);
                          setActionMenuOpen(null);
                        }} className="block w-full text-left px-4 py-2 text-sm text-red-700 hover:bg-red-50" data-id="element-20813">
                                  <TrashIcon className="h-4 w-4 inline mr-2" data-id="element-20814" />
                                  Delete User
                                </button>
                              </div>
                            </div>}
                        </div>
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>}
      {/* User Groups Tab */}
      {activeTab === 'groups' && <div data-id="element-20815">
          {/* Search and Actions */}
          <div className="mb-6 flex justify-between items-center" data-id="element-20816">
            <div className="flex-1 max-w-md" data-id="element-20817">
              <div className="relative rounded-md shadow-sm" data-id="element-20818">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-20819">
                  <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-20820" />
                </div>
                <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" placeholder="Search groups..." data-id="element-20821" />
              </div>
            </div>
            <button onClick={handleAddGroup} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-20822">
              <PlusIcon className="h-4 w-4 mr-1" data-id="element-20823" />
              Add Group
            </button>
          </div>
          {/* Groups Grid */}
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3" data-id="element-20824">
            {filteredGroups.map(group => <div key={group.id} className="bg-white overflow-hidden shadow rounded-lg hover:shadow-md transition-shadow" data-id="element-20825">
                <div className="p-5" data-id="element-20826">
                  <div className="flex items-center justify-between" data-id="element-20827">
                    <div className="flex items-center" data-id="element-20828">
                      <div className="flex-shrink-0" data-id="element-20829">
                        <UsersIcon className="h-8 w-8 text-indigo-600" data-id="element-20830" />
                      </div>
                      <div className="ml-3" data-id="element-20831">
                        <h3 className="text-lg font-medium text-gray-900" data-id="element-20832">
                          {group.name}
                        </h3>
                        <p className="text-sm text-gray-500" data-id="element-20833">
                          {group.memberCount} members
                        </p>
                      </div>
                    </div>
                    <button className="text-gray-400 hover:text-gray-600" data-id="element-20834">
                      <MoreVerticalIcon className="h-5 w-5" data-id="element-20835" />
                    </button>
                  </div>
                  <p className="mt-3 text-sm text-gray-600" data-id="element-20836">
                    {group.description}
                  </p>
                  <div className="mt-4" data-id="element-20837">
                    <div className="text-xs text-gray-500 mb-2" data-id="element-20838">
                      Permissions:
                    </div>
                    <div className="flex flex-wrap gap-1" data-id="element-20839">
                      {group.permissions.slice(0, 3).map(perm => <span key={perm} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800" data-id="element-20840">
                          {perm}
                        </span>)}
                      {group.permissions.length > 3 && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800" data-id="element-20841">
                          +{group.permissions.length - 3} more
                        </span>}
                    </div>
                  </div>
                  <div className="mt-4 flex justify-between items-center" data-id="element-20842">
                    <span className="text-xs text-gray-500" data-id="element-20843">
                      Created {formatDate(group.createdAt)}
                    </span>
                    <div className="flex space-x-2" data-id="element-20844">
                      <button onClick={() => handleEditGroup(group)} className="text-indigo-600 hover:text-indigo-900 text-sm font-medium" data-id="element-20845">
                        Edit
                      </button>
                      <button onClick={() => handleDeleteGroup(group)} className="text-red-600 hover:text-red-900 text-sm font-medium" data-id="element-20846">
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>)}
          </div>
        </div>}
      {/* Permissions Editor Modal */}
      {showPermissionsEditor && <PermissionsEditor onClose={() => {
      setShowPermissionsEditor(false);
      setSelectedUser(null);
    }} onSave={handleSavePermissions} assignedModules={selectedUser?.assignedModules || []} licensedModules={licensedModules} data-id="element-20847" />}
      {/* Add User Modal Placeholder */}
      {showUserForm && <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50" data-id="element-20848">
          <div className="bg-white rounded-lg p-6 max-w-md w-full" data-id="element-20849">
            <div className="flex justify-between items-center mb-4" data-id="element-20850">
              <h3 className="text-lg font-medium text-gray-900" data-id="element-20851">
                Add New User
              </h3>
              <button onClick={() => setShowUserForm(false)} className="text-gray-400 hover:text-gray-500" data-id="element-20852">
                <XIcon className="h-6 w-6" data-id="element-20853" />
              </button>
            </div>
            <p className="text-sm text-gray-500 mb-4" data-id="element-20854">
              User form would be displayed here with fields for name, email,
              role, department, etc.
            </p>
            <div className="flex justify-end space-x-2" data-id="element-20855">
              <button onClick={() => setShowUserForm(false)} className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50" data-id="element-20856">
                Cancel
              </button>
              <button onClick={() => {
            alert('User would be created');
            setShowUserForm(false);
          }} className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700" data-id="element-20857">
                Create User
              </button>
            </div>
          </div>
        </div>}
      {/* Add Group Modal Placeholder */}
      {showGroupForm && <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50" data-id="element-20858">
          <div className="bg-white rounded-lg p-6 max-w-md w-full" data-id="element-20859">
            <div className="flex justify-between items-center mb-4" data-id="element-20860">
              <h3 className="text-lg font-medium text-gray-900" data-id="element-20861">
                Add New Group
              </h3>
              <button onClick={() => setShowGroupForm(false)} className="text-gray-400 hover:text-gray-500" data-id="element-20862">
                <XIcon className="h-6 w-6" data-id="element-20863" />
              </button>
            </div>
            <p className="text-sm text-gray-500 mb-4" data-id="element-20864">
              Group form would be displayed here with fields for name,
              description, permissions, etc.
            </p>
            <div className="flex justify-end space-x-2" data-id="element-20865">
              <button onClick={() => setShowGroupForm(false)} className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50" data-id="element-20866">
                Cancel
              </button>
              <button onClick={() => {
            alert('Group would be created');
            setShowGroupForm(false);
          }} className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700" data-id="element-20867">
                Create Group
              </button>
            </div>
          </div>
        </div>}
    </div>;
}